import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  IconButton,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  useDeletePortfolioMutation,
  useLazyGetPortfolioListQuery,
} from "../../service/portfolio";
import { PortfolioDetails } from "../../types/General";
import { showError, showToast } from "../../constants/toasts";
import { getFromStorage, STORAGE_KEYS } from "../../helpers";
import dayjs from "dayjs";
import Loader from "../../constants/Loader";
import CustomPagination from "../../components/CustomPagination";
import WarnModal from "../../Modals/warnmodal";
import { isValidInput } from "../../utils/validations";


const Portfolio = () => {
  const navigate = useNavigate();
  const [getPortfolioList] = useLazyGetPortfolioListQuery();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [portfolios, setPortfolios] = useState<PortfolioDetails[]>([]);
  const token = getFromStorage(STORAGE_KEYS.token);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [open, setOpen] = useState<boolean>(false);
  const [count, setCount] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [deletePortfolioMethod] = useDeletePortfolioMutation();
  const handleDelete = async (
    deleteById: any,
    selectedId: string,
    getAllData: () => void,
  ) => {
    if (selectedId) {
      try {
        const response = await deleteById({ id: selectedId }).unwrap();
        if (response?.statusCode === 200) {
          showToast(`Deleted Successfully`);
          if (getAllData) {
            getAllData();
          }
        }
      } catch (error: any) {
        showError(error?.data?.message || "");
      }
    }
  };
  const getPortfolioListApi = async () => {
    try {
      setLoading(true);
      const response = await getPortfolioList({
        page: page,
        limit: limit,
        search: debouncedSearchTerm,
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setPortfolios(response?.data?.list || []);
        setCount(response?.data?.total || 0);
      } else {
        setPortfolios([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    getPortfolioListApi();
  }, [page, limit, debouncedSearchTerm]);

  return (
    <>
      <Loader isLoad={loading} />
      <div className="main_title">
        <h1>Portfolio Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Portfolio Management
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: any) => isValidInput(e.target.value) && setSearchTerm(e.target.value)}
              onCross={() => {
                setSearchTerm("");
                setDebouncedSearchTerm("");
              }}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              placeholder="Search Service Name..."
            />
          </div>
          <div className="right_s">
            <Button className="btn btn_primary" onClick={() => navigate("add")}>
              <AddIcon /> Add Portfolio
            </Button>
          </div>
        </div>

        <TableContainer className="table_container" sx={{ mt: 3 }}>
          <Table
            sx={{ minWidth: 750 }}
            aria-label="portfolio table"
            className="v3"
          >
            <TableHead>
              <TableRow hover>
                <TableCell>Thumbnail</TableCell>
                <TableCell>Service Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Sub-category</TableCell>
                <TableCell>Date of Completion</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {portfolios.length > 0 ? (
                portfolios?.map((portfolio: any, index) => (
                  <TableRow hover key={portfolio?.id ?? index}>
                    {/* Thumbnail */}
                    <TableCell>
                      <div className="user_block">
                        <figure>
                          <img
                            src={portfolio?.media?.find((m: any) => String(m.type) === "1")?.url || "/static/images/placeholder.png"}
                            alt={portfolio?.name || "portfolio"}
                          />
                        </figure>
                      </div>
                    </TableCell>

                    {/* Title */}
                    <TableCell>
                      <strong>{portfolio?.name || "-"}</strong>
                    </TableCell>

                    {/* Category */}
                    <TableCell>{portfolio?.category?.name || "-"}</TableCell>

                    {/* Sub-category */}
                    <TableCell>{portfolio?.subCategory?.name || "-"}</TableCell>

                    {/* Completion Date */}
                    <TableCell>
                      {portfolio?.createdAt
                        ? dayjs(portfolio?.date).format("DD MMMM YYYY")
                        : "-"}
                    </TableCell>

                    {/* Action Buttons */}
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton
                          onClick={() =>
                            navigate(`/portfolio/view/${portfolio?.id}`)
                          }
                        >
                          <img
                            src="/static/images/eye_icon.svg"
                            alt="View"
                            style={{ width: 22, height: 22 }}
                          />
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            navigate(`/portfolio/edit/${portfolio?.id}`)
                          }
                        >
                          <img
                            src="/static/images/edit2_icon.svg"
                            alt="Edit"
                            style={{ width: 22, height: 22 }}
                          />
                        </IconButton>

                        <IconButton
                          onClick={() => {
                            setSelectedId(portfolio?.id);
                            setOpen(true);
                          }}
                        >
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Delete"
                            style={{ width: 22, height: 22 }}
                          />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No portfolio items found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {portfolios?.length ? (
          <CustomPagination
            currentPage={page - 1}
            totalPages={Math.ceil(count / limit)}
            count={count}
            pageSize={limit}
            onPageChange={(page) => setPage(page + 1)}
          />
        ) : null}
      </div>
      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={() =>
          handleDelete(deletePortfolioMethod, selectedId, getPortfolioListApi)
        }
        name="portfolio"
      />
    </>
  );
};

export default Portfolio;
